import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/api";
import { ShieldCheck, Loader2, Download, Radio, MapPin, Video, QrCode, ChevronRight, BellRing, Tv, Truck } from "lucide-react";
import { motion } from "framer-motion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { QRCodeCanvas } from "qrcode.react";
import { LogoMark } from "@/context/BrandingContext";
import { toast } from "sonner";

const lContainer = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.15 } } };
const lItem = { hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } } };
const FEAT_ICONS = [Radio, MapPin, Video, ShieldCheck];
const QR_APPS = [
  { key: "pager", path: "/pager", icon: BellRing },
  { key: "monitor", path: "/kazerne", icon: Tv },
  { key: "voertuig", path: "/voertuig", icon: Truck },
];

export default function Login() {
  const { login, completeMfa } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [mfa, setMfa] = useState(null);
  const [code, setCode] = useState("");
  const [deferred, setDeferred] = useState(null);
  const [standalone, setStandalone] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [qrApp, setQrApp] = useState(null);

  const maximize = () => {
    const el = document.documentElement;
    if (el.requestFullscreen && !document.fullscreenElement) el.requestFullscreen().catch(() => {});
  };

  useEffect(() => {
    const mm = window.matchMedia("(display-mode: standalone)");
    setStandalone(mm.matches || window.navigator.standalone === true);
    const onBip = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setDeferred(null); maximize(); };
    window.addEventListener("beforeinstallprompt", onBip);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBip);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // Installed app (kiosk): maximize to fullscreen on the first user gesture.
  useEffect(() => {
    if (!standalone) return undefined;
    const go = () => maximize();
    window.addEventListener("click", go, { once: true });
    return () => window.removeEventListener("click", go);
  }, [standalone]);

  const install = async () => {
    maximize();
    if (deferred) {
      deferred.prompt();
      try { await deferred.userChoice; } catch (e) {}
      setDeferred(null);
    } else if (!standalone) {
      toast.info(t("login.installHint"));
    }
  };

  const goHome = (u) => { toast.success(t("login.welcome", { name: u.name })); navigate(u.role === "voertuig" ? "/voertuig" : "/dashboard"); };

  const submit = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      const data = await login(email, password);
      if (data.mfa_required) { setMfa(data); setCode(""); }
      else goHome(data.user);
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  const verify = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try { const u = await completeMfa(mfa.temp_token, code); goHome(u); }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="flex min-h-screen bg-slate-900">
      {/* Left brand panel */}
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-[#030712] p-12 lg:flex">
        <div className="pointer-events-none absolute inset-0 opacity-[0.05]" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)", backgroundSize: "48px 48px" }} />
        <div className="pointer-events-none absolute -left-24 -top-24 h-[440px] w-[440px] rounded-full blur-[130px]" style={{ background: "radial-gradient(circle, rgba(37,99,235,0.32) 0%, transparent 70%)" }} />
        <div className="pointer-events-none absolute -bottom-24 right-0 h-[360px] w-[360px] rounded-full blur-[120px]" style={{ background: "radial-gradient(circle, rgba(16,185,129,0.15) 0%, transparent 70%)" }} />

        <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="relative flex items-center gap-3">
          <LogoMark size={44} rounded="rounded-xl" />
          <div>
            <div className="font-display text-lg font-bold text-white">DispatchMe</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-blue-400">{t("login.defenseCenter")}</div>
          </div>
        </motion.div>

        <motion.div variants={lContainer} initial="hidden" animate="show" className="relative">
          <motion.span variants={lItem} className="inline-flex w-fit items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 font-mono text-[11px] font-medium uppercase tracking-[0.15em] text-blue-300">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 live-dot" /> {t("login.badge")}
          </motion.span>
          <motion.h1 variants={lItem} className="mt-6 font-display text-4xl font-extrabold leading-[1.1] tracking-tight text-white xl:text-5xl" style={{ whiteSpace: "pre-line" }}>
            {t("login.headline")}
          </motion.h1>
          <motion.p variants={lItem} className="mt-5 max-w-md text-sm leading-relaxed text-slate-400">
            {t("login.tagline")}
          </motion.p>
          <motion.ul variants={lItem} className="mt-8 space-y-3.5">
            {(t("login.features", { returnObjects: true }) || []).map((f, i) => {
              const Icon = FEAT_ICONS[i] || ShieldCheck;
              return (
                <li key={i} className="flex items-center gap-3 text-sm text-slate-300">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-blue-400">
                    <Icon className="h-4 w-4" />
                  </span>
                  {f}
                </li>
              );
            })}
          </motion.ul>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }}
          className="relative flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 backdrop-blur">
          <span className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-emerald-400">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 live-dot" /> {t("login.live")}
          </span>
          <span className="font-mono text-[11px] text-slate-600">{t("login.prototype")}</span>
        </motion.div>
      </div>

      {/* Right form */}
      <div className="flex w-full items-center justify-center p-6 lg:w-1/2">
        <div className="w-full max-w-sm">
          <div className="mb-6 flex items-center justify-between lg:justify-end">
            <div className="flex items-center gap-2 lg:hidden">
              <ShieldCheck className="h-7 w-7 text-blue-500" />
              <span className="font-display text-xl font-bold text-white">DispatchMe</span>
            </div>
            <LanguageSwitcher dark />
          </div>
          <h2 className="font-display text-2xl font-bold text-white">{t("login.title")}</h2>
          <p className="mt-1 text-sm text-slate-400">{t("login.subtitle")}</p>

          {mfa ? (
            <form onSubmit={verify} className="mt-8 space-y-4" data-testid="mfa-form">
              {mfa.setup && (
                <div className="rounded-xl border border-slate-800 bg-white p-4 text-center">
                  <div className="mb-2 text-xs font-semibold text-slate-700">{t("login.scanAuth")}</div>
                  <div className="flex justify-center"><QRCodeCanvas value={mfa.setup.otpauth_uri} size={148} /></div>
                  <div className="mt-2 break-all font-mono text-[10px] text-slate-400">{mfa.setup.secret}</div>
                </div>
              )}
              <p className="text-sm text-slate-400">{mfa.mode === "email" ? t("login.mfaEmail") : t("login.mfaApp")}</p>
              <input data-testid="mfa-code" value={code} onChange={(e) => setCode(e.target.value)} required inputMode="numeric" maxLength={6}
                className="w-full rounded-xl border border-slate-700 bg-slate-800 px-4 py-3 text-center font-mono text-lg tracking-[0.4em] text-white outline-none focus:border-blue-500" placeholder="______" />
              {error && <div data-testid="login-error" className="rounded-lg bg-red-500/10 px-3 py-2 text-sm text-red-400">{error}</div>}
              <button data-testid="mfa-submit" type="submit" disabled={loading} className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 py-3 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-60">{loading && <Loader2 className="h-4 w-4 animate-spin" />} {t("login.verify")}</button>
              <button type="button" onClick={() => { setMfa(null); setError(""); }} className="w-full text-center text-xs text-slate-500 hover:text-slate-300">← {t("common.back")}</button>
            </form>
          ) : (
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-400">{t("login.email")}</label>
              <input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full rounded-xl border border-slate-700 bg-slate-800 px-4 py-3 text-sm text-white placeholder-slate-500 outline-none focus:border-blue-500"
                placeholder="naam@dhc.nl"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-400">{t("login.password")}</label>
              <input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full rounded-xl border border-slate-700 bg-slate-800 px-4 py-3 text-sm text-white placeholder-slate-500 outline-none focus:border-blue-500"
                placeholder="••••••••"
              />
            </div>
            {error && <div data-testid="login-error" className="rounded-lg bg-red-500/10 px-3 py-2 text-sm text-red-400">{error}</div>}
            <button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 py-3 text-sm font-semibold text-white transition-colors hover:bg-blue-500 disabled:opacity-60"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />} {t("common.login")}
            </button>
            <div className="text-center">
              <button type="button" data-testid="forgot-password-link" onClick={() => navigate("/wachtwoord-vergeten")} className="text-xs text-slate-400 hover:text-slate-200">{t("login.forgot")}</button>
            </div>
          </form>
          )}

          <button type="button" data-testid="install-app-button" onClick={install}
            className="mt-8 flex w-full items-center justify-center gap-2 rounded-xl border border-blue-500/40 bg-blue-600/15 py-3 text-sm font-semibold text-blue-200 transition-colors hover:bg-blue-600/25">
            <Download className="h-4 w-4" /> {t("login.installApp")}
          </button>

          <button type="button" data-testid="open-app-qr" onClick={() => { setQrApp(null); setQrOpen(true); }}
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl border border-slate-800 bg-slate-800/40 py-3 text-sm font-semibold text-slate-300 transition-colors hover:bg-slate-800/70">
            <QrCode className="h-4 w-4" /> {t("login.scanApps")}
          </button>

          <Dialog open={qrOpen} onOpenChange={setQrOpen}>
            <DialogContent data-testid="app-qr-dialog" className="border-slate-700 bg-slate-900 text-white sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="text-white">{qrApp ? t(`login.scan.apps.${qrApp.key}.label`) : t("login.scan.title")}</DialogTitle>
                <DialogDescription className="text-slate-400">{qrApp ? t("login.scan.instruct") : t("login.scan.subtitle")}</DialogDescription>
              </DialogHeader>
              {!qrApp ? (
                <div className="space-y-2">
                  {QR_APPS.map((a) => (
                    <button key={a.key} data-testid={`choose-app-${a.key}`} onClick={() => setQrApp(a)}
                      className="flex w-full items-center gap-3 rounded-xl border border-slate-700 bg-slate-800/60 p-3 text-left transition-colors hover:bg-slate-800">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-600/20 text-blue-300"><a.icon className="h-5 w-5" /></span>
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-white">{t(`login.scan.apps.${a.key}.label`)}</span>
                        <span className="block text-xs text-slate-400">{t(`login.scan.apps.${a.key}.desc`)}</span>
                      </span>
                      <ChevronRight className="ml-auto h-4 w-4 shrink-0 text-slate-500" />
                    </button>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center gap-4 py-2">
                  <div className="rounded-2xl bg-white p-4" data-testid="app-qr-canvas">
                    <QRCodeCanvas value={`${window.location.origin}${qrApp.path}`} size={220} />
                  </div>
                  <div className="w-full break-all rounded-lg bg-slate-800 px-3 py-2 text-center font-mono text-xs text-slate-300">{window.location.origin}{qrApp.path}</div>
                  <div className="flex w-full items-center gap-2">
                    <button data-testid="app-qr-back" onClick={() => setQrApp(null)} className="flex-1 rounded-xl border border-slate-700 py-2.5 text-sm font-semibold text-slate-300 hover:bg-slate-800">{t("login.scan.back")}</button>
                    <a data-testid="app-qr-open" href={qrApp.path} target="_blank" rel="noreferrer" className="flex-1 rounded-xl bg-blue-600 py-2.5 text-center text-sm font-semibold text-white hover:bg-blue-500">{t("login.scan.open")}</a>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
}
